// composables/useApi.js - 공통 API 호출 (토큰, 로그인 만료 처리)
import { useAuthState } from './useAuthState'
import { useGuestAuth } from './useGuestAuth'

// fetch 는 기본 타임아웃이 없다. 패킷이 끊기는 구간에서는 응답도 에러도 오지 않아
// 화면이 로딩을 켠 채로 영구히 멈춘다. 그 상태를 끊어주는 값이다.
// 서버 RestTemplate 이 외부 연동에 30초를 쓰지만, 사용자를 그만큼 세워둘 이유는 없다.
// 첨부파일 업로드는 useApi 를 거치지 않으므로(multipart 라 직접 전송) 이 값에 걸리지 않는다.
const API_TIMEOUT_MS = 15000

// 요청 로그에 인증번호·비밀번호(PIN)가 그대로 찍히지 않게 가린다. (실제 전송 값은 그대로)
const SECRET_KEYS = ['authCode', 'pin', 'pinConfirm', 'newPin', 'oldPin', 'password', 'accessToken', 'idToken', 'code']
const maskSecrets = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return data
  const masked = { ...data }
  SECRET_KEYS.forEach((key) => {
    if (masked[key] != null && masked[key] !== '') masked[key] = '***'
  })
  return masked
}

// 비회원도 볼 수 있는 홈페이지 화면. 토큰이 만료돼도 로그인 화면으로 보내지 않고 비회원으로 다시 연다.
// middleware/homepage.global.js 의 공개 화면 목록도 이 목록을 쓴다.
export const HOMEPAGE_GUEST_VIEW_PAGES = ['/homepage', '/homepage/magazine']

const publicApis = [
  '/api/auth/readMagazine',
  '/api/auth/sendAuthCode',
  '/api/auth/checkAuthCode',
  '/api/auth/sendEmailLoginCode',
  '/api/auth/captcha',
  '/api/auth/checkCaptcha',
  '/api/auth/readTermList',
  '/api/auth/join',
  '/api/auth/login',
  '/api/auth/getSnsId',
  '/api/auth/naver/token',
  '/api/auth/google/token',
  '/api/refund/getRefundStatus',
  '/api/refund/addRefund',
  '/api/family/createGuestLink',
  '/api/family/checkGuestInvite',
  '/api/cs/readFaqList',
  '/api/device/readLockScreen',
  '/api/device/createUsimHistory',
]

/** 게스트 화면(/guest)에서만 gst_ 를 붙이는 API. 호스트 화면에서는 호스트 토큰을 쓴다. */
const guestApis = [
  '/api/family/readGuestHome',
  '/api/device/readAppList',
  '/api/device/updateMyDeviceAppList',
  '/api/device/readMyBlockAppList',
  '/api/device/updateMyBlockApp',
  '/api/device/updateMyBlockAllYn',
  '/api/device/checkMyDeviceSecret',
  '/api/device/reissueMyDeviceSecret',
  '/api/device/readMyUsimHistory',
  '/api/user/createPin',
  '/api/user/checkPin',
  '/api/user/updatePin',
  '/api/user/isPinSet',
]

export const useApi = () => { 
  const config = useRuntimeConfig()
  const route = useRoute()
  const router = useRouter()
  const authState = useAuthState()
  const guestAuth = useGuestAuth()

  const isGuestRoute = () => isGuestPath(route.path)

  async function handleUnauthorized() {
    if (!import.meta.client) return

    if (isGuestRoute()) {
      guestAuth.clearGuestAuth()
      await navigateTo("/guest/invite-code", { replace: true })
      return
    }

    authState.clearAuth()

    // 홈페이지 SNS 연동 콜백(/auth/*/callback?state=homepage-connect...)은 콜백이 홈페이지 로그인 화면으로 보낸다.
    if (route.path.includes('/auth/') && looksLikeHomepageSnsState(route.query.state)) return

    // 홈페이지(PC 웹)는 앱과 같은 토큰을 쓰지만 로그인 화면이 따로 있다.
    // 비회원도 볼 수 있는 홈·매거진은 로그인 화면으로 보내지 않고 비회원 화면으로 다시 연다. (화면에 남은 회원 정보도 새로 불러오며 지워진다)
    if (HOMEPAGE_GUEST_VIEW_PAGES.includes(route.path.replace(/(.)\/+$/, "$1"))) {
      await navigateTo(router.resolve(route.fullPath).href, { external: true, replace: true })
      return
    }
    if (isHomepagePath(route.path)) {
      await navigateTo("/homepage/login", { replace: true })
      return
    }

    // uPass·kPass 웹뷰만 자체 인증 실패 화면(/authfail)을 쓴다.
    // 폰지킴 앱과 웹은 로그인 화면으로 보낸다. 커버 코드가 늘어나도
    // 실재하는 /login 이 기본이 되도록 authfail 쪽을 화이트리스트로 둔다.
    const coverGbnCd = sessionStorage.getItem("coverGbnCd");
    const useAuthFailPage = coverGbnCd === "UPASSAPP" || coverGbnCd === "KPASSAPP"
    const redirectPath = useAuthFailPage ? "/authfail" : "/login"

    console.log("토큰이 없거나 유효시간 만료로 로그인 실패")

    await navigateTo(redirectPath)
  }

  const api = async (url, data = {}, options = {}) => {
  // 오래 걸리는 것이 정상인 호출만 options.timeoutMs 로 따로 늘려 쓴다.
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), options.timeoutMs || API_TIMEOUT_MS)
  try {
    const token = authState.token.value
    const guestToken = guestAuth.token.value
    const isPublicApi = publicApis.some(path => url.includes(path))
    const isGuestApi = guestApis.some(path => url.includes(path))
    const useGuestToken = !isPublicApi && isGuestApi && !!guestToken && isGuestRoute()

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    if (useGuestToken) {
      headers.Authorization = `Bearer ${guestToken}`
    } else if (!isPublicApi && token) {
      headers.Authorization = `Bearer ${token}`
    }

    const apiPath = url.startsWith('/api') ? url : `/api${url}`
    const fullUrl = `${config.public.apiServer}${apiPath}`

    const method = options.method || 'POST';

    console.log('🔍 API 호출:', fullUrl)
    console.log('🔍 Method:', method)
    console.log('🔍 Data:', maskSecrets(data))

    const fetchOptions = {
      method: method,
      headers,
      credentials: 'include',
      signal: controller.signal,
    }

    // ✅ POST/PUT/DELETE는 항상 body 포함 (빈 객체도 전송)
    if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
      fetchOptions.body = JSON.stringify(data || {});
    }

    const response = await fetch(fullUrl, fetchOptions)

    console.log('✅ 응답 상태:', response.status)

    const contentType = response.headers.get('content-type')
    let result

    if (contentType && contentType.includes('application/json')) {
      result = await response.json()
    } else {
      const text = await response.text()
      result = { resCd: '9999', resMsg: text }
    }

    if (response.status === 401 || result.resCd === "UNAUTHORIZED") {
      if (!options.skipErrorHandler) {
        await handleUnauthorized()
      }
      throw new Error('UNAUTHORIZED')
    }

    return result

  } catch (error) {
    console.error('❌ API 에러:', error)
    // 끊은 요청을 throw 로 올리면, catch 가 없는 화면에서는 로딩만 사라지고 아무 안내도 뜨지 않는다.
    // 다른 실패와 같은 모양(resCd 9999)으로 돌려서 화면의 기존 실패 처리 경로를 그대로 타게 한다.
    if (error?.name === 'AbortError') {
      return { resCd: '9999', resMsg: '응답이 지연되고 있습니다. 네트워크 상태를 확인하고 다시 시도해주세요.' }
    }
    if (error.data) {
      return error.data
    }
    throw error
  } finally {
    // 본문을 읽는 시간도 타임아웃에 포함된다. 헤더만 오고 본문이 멈추는 경우까지 끊어야 한다.
    clearTimeout(timeoutId)
  }
}
  
  return { api }
}